import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { InboxAdapter, InboxFetchOptions, InboxFilter, InboxRowItem, MessageType, cursorOf } from './types';
import InboxCard, { IconButton, TYPE_META } from './InboxCard';
import InboxGroupHeader from './InboxGroupHeader';
import InboxPreviewPanel from './InboxPreviewPanel';
import ActivityModal from '../app-activity-modal/ActivityModal';
import Modal from '../../components/shared/Modal/Modal';
import FilteringControl from '../../components/shared/FilteringControl/FilteringControl';
import SearchBarWithActions from '../../components/shared/SearchBarWithActions/SearchBarWithActions';
import FiltersDropdown from '../../components/shared/FiltersDropdown/FiltersDropdown';
import styles from './InboxTab.module.scss';

type Filter = InboxFilter;

type Props<Item extends InboxRowItem> = {
  adapter: InboxAdapter<Item>;
};

const InboxTab = <Item extends InboxRowItem>({ adapter }: Props<Item>) => {
  const [t] = useTranslation('dashboard');
  const { groupKey } = adapter;

  // Filter options derive from TYPE_META so filter labels and badge labels can't drift.
  const typeOptions = useMemo(
    () => adapter.messageTypes.map((value) => ({ value, labelKey: TYPE_META[value].labelKey, fallback: TYPE_META[value].fallback })),
    [adapter],
  );

  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>();

  const [activeFilter, setActiveFilter] = useState<Filter>('all');
  // Type + project facets live behind the single "Filter" popover (FiltersDropdown).
  // filtersData is the committed selection; FiltersDropdown applies on "Spara".
  const [filtersData, setFiltersData] = useState<{ messageType: MessageType[]; projects: string[] }>({ messageType: [], projects: [] });
  const [showFilter, setShowFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [activityItem, setActivityItem] = useState<Item | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupLoading, setGroupLoading] = useState<Set<string>>(new Set());

  const sentinelRef = useRef<HTMLDivElement>(null);
  // Monotonic id of the most recently issued fetch — see fetchPage's guard.
  const requestSeqRef = useRef(0);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const dedupeById = (arr: Item[]): Item[] => {
    const seen = new Set<string>();
    return arr.filter(i => { if (seen.has(i.conversationId)) return false; seen.add(i.conversationId); return true; });
  };

  const fetchPage = useCallback(async (opts: InboxFetchOptions & { append?: boolean }): Promise<Item[] | null> => {
    // Sequence guard: only the latest-issued request may apply its result. A
    // superseded or out-of-order response (e.g. a stale debounced-search fetch,
    // or a slow load-more resolving after a filter change) is dropped so it can't
    // clobber the list.
    const seq = ++requestSeqRef.current;
    setLoading(true);
    try {
      const data = await adapter.fetchPage(opts);
      if (seq !== requestSeqRef.current) return null; // superseded by a newer request
      if (opts.append) {
        setItems(prev => dedupeById([...prev, ...data.items]));
      } else {
        setItems(data.items);
      }
      setHasMore(data.hasMore);
      setNextCursor(data.nextCursor);
      return data.items;
    } catch {
      return null;
    } finally {
      // Only the latest request owns the loading flag; a superseded one leaves it
      // set for the request that overtook it.
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [adapter]);

  // Current filter/project/search selection, shared by every fetchPage call site
  // below (each can override individual keys, e.g. a fresher `q` or a `cursor`).
  const currentFetchOpts = useCallback((extra: Partial<InboxFetchOptions & { append?: boolean }> = {}) => ({
    filter: activeFilter, messageTypes: filtersData.messageType, projectIds: filtersData.projects, q: searchQuery, ...extra,
  }), [activeFilter, filtersData, searchQuery]);

  // After a full refresh (page 1 reload), re-select the open conversation by id
  // if it's still in the result set, or close the panel if it dropped off
  // (e.g. it was on a later page before the refresh).
  const refresh = useCallback(async () => {
    const newItems = await fetchPage(currentFetchOpts());
    if (newItems) {
      // Drop any expanded-group keys that no longer appear in the refreshed page.
      const liveKeys = new Set(newItems.map(groupKey));
      setExpandedGroups(prev => {
        const pruned = new Set(Array.from(prev).filter(k => liveKeys.has(k)));
        return pruned.size === prev.size ? prev : pruned;
      });
      setGroupLoading(new Set());
      setSelectedItem(prev => prev
        ? newItems.find(i => i.conversationId === prev.conversationId) ?? null
        : null
      );
    }
  }, [currentFetchOpts, fetchPage, groupKey]);

  // Fetch the next page. Triggered both by the scroll sentinel and by keyboard
  // navigation nearing the end of the loaded list (where the sentinel never
  // scrolls into view because scrollIntoView stops at the last row).
  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;
    fetchPage(currentFetchOpts({ cursor: nextCursor, append: true }));
  }, [hasMore, loading, nextCursor, currentFetchOpts, fetchPage]);

  const loadMoreForGroup = useCallback(async (key: string, groupItems: Item[]) => {
    const lastItem = groupItems[groupItems.length - 1];
    if (!lastItem) return;
    const cursor = cursorOf(lastItem);

    setGroupLoading(prev => new Set(prev).add(key));
    try {
      const data = await adapter.fetchGroupPage(lastItem, currentFetchOpts({ cursor }));
      if (data.items.length > 0) {
        setItems(prev => dedupeById([...prev, ...data.items]));
      }
    } catch {
      // silent — no state corruption
    } finally {
      setGroupLoading(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
  }, [adapter, currentFetchOpts]);

  useEffect(() => {
    // A committed filter/facet change owns the next fetch — cancel any pending
    // debounced search first, otherwise its older, stale-filter fetch could fire
    // ~800ms later and clobber the list (showing results that contradict the
    // active filter). The sequence guard in fetchPage backs this up.
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSelectedItem(null);
    fetchPage(currentFetchOpts());
    // Intentionally excludes searchQuery/currentFetchOpts — a search-only change
    // is handled by the debounced handleSearchChange below, not this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, filtersData]);

  // Clear any pending debounced search on unmount.
  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  const handleSearchChange = (q: string) => {
    setSearchQuery(q);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSelectedItem(null);
      fetchPage(currentFetchOpts({ q }));
    }, 800);
  };

  useEffect(() => {
    if (!sentinelRef.current || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadMore();
    }, { threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  // Accumulate project options so the dropdown never shrinks when the list is
  // filtered — otherwise selecting one project removes the others as options
  // and you can't build a multi-project filter.
  const [projectOptions, setProjectOptions] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    setProjectOptions(prev => {
      const map = new Map(prev.map(p => [p.id, p.name]));
      let changed = false;
      items.forEach(i => {
        if (i.projectId && !map.has(i.projectId)) { map.set(i.projectId, i.projectName); changed = true; }
      });
      return changed ? Array.from(map.entries()).map(([id, name]) => ({ id, name })) : prev;
    });
  }, [items]);

  // Type + project facets for the Filter popover (FiltersDropdown). Collapsible,
  // multi-select sections; the funnel button shows an active-filter count badge.
  const filterCheckboxes = useMemo(() => [
    {
      label: t('inbox.filter.typeLabel', 'Meddelandetyp'),
      name: 'messageType',
      value: filtersData.messageType,
      subChecks: typeOptions.map(o => ({ label: t(o.labelKey, o.fallback), name: 'messageType', value: o.value })),
    },
    {
      label: t('inbox.filter.projectLabel', 'Projekt'),
      name: 'projects',
      value: filtersData.projects,
      subChecks: projectOptions.map(p => ({ label: p.name, name: 'projects', value: p.id })),
    },
  ], [t, filtersData, projectOptions, typeOptions]);

  const handleSubmitFilters = useCallback((data: { messageType?: string[]; projects?: string[] }) => {
    setSelectedItem(null);
    setFiltersData({
      messageType: adapter.messageTypes.filter(type => data.messageType?.includes(type)),
      projects: data.projects ?? [],
    });
  }, [adapter]);

  const handleResetFilters = useCallback(() => {
    setSelectedItem(null);
    setShowFilter(false);
    setFiltersData({ messageType: [], projects: [] });
  }, []);

  // Group conversations by the adapter's key (Outlook-style). `items` is already
  // sorted newest-first, so each group's first entry is its latest.
  const groups = useMemo(() => {
    const map = new Map<string, Item[]>();
    items.forEach(i => {
      const key = groupKey(i);
      const arr = map.get(key);
      if (arr) arr.push(i); else map.set(key, [i]);
    });
    return Array.from(map.entries()).map(([key, groupItems]) => ({ key, items: groupItems }));
  }, [items, groupKey]);

  // Groups default to collapsed — on initial load and on loading more (scroll/
  // pagination) alike. Expansion only happens via explicit user toggle.

  // Keyboard-navigable rows: expanded groups contribute every child; collapsed
  // groups contribute their latest item as a proxy so Up/Down steps through
  // every conversation without skipping over collapsed suppliers.
  const visibleItems = useMemo(() => {
    const flat: Item[] = [];
    groups.forEach(g => {
      if (expandedGroups.has(g.key)) flat.push(...g.items);
      else flat.push(g.items[0]);
    });
    return flat;
  }, [groups, expandedGroups]);

  // loadMore's identity changes on every fetch (its `loading`/`hasMore`/`nextCursor`
  // deps flip mid-request), which would otherwise tear down and re-add the global
  // keydown listener on every page load. Read it via a ref so the listener effect
  // only depends on things that actually affect keyboard-nav behavior.
  const loadMoreRef = useRef(loadMore);
  useEffect(() => { loadMoreRef.current = loadMore; });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedItem) return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;

      // ArrowLeft/Right: collapse/expand the group containing selectedItem.
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const group = groups.find(
          g => g.items.some(i => i.conversationId === selectedItem.conversationId)
        );
        if (!group) return;
        e.preventDefault();
        const isExp = expandedGroups.has(group.key);
        if (e.key === 'ArrowRight' && !isExp) {
          toggleGroup(group.key);
        } else if (e.key === 'ArrowLeft' && isExp) {
          toggleGroup(group.key);
          setSelectedItem(group.items[0]); // select latest after collapse
        }
        return;
      }

      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const idx = visibleItems.findIndex(i => i.conversationId === selectedItem.conversationId);
      if (idx === -1) return;
      const nextIdx = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
      if (nextIdx >= 0 && nextIdx < visibleItems.length) setSelectedItem(visibleItems[nextIdx]);
      // Prefetch the next page as arrow-down nears the end — the scroll sentinel
      // never comes into view via keyboard, so it can't trigger the load itself.
      if (e.key === 'ArrowDown' && nextIdx >= visibleItems.length - 3) loadMoreRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedItem, visibleItems, groups, expandedGroups, toggleGroup]);

  // Keep the selected row in view as you arrow through the list. `block: 'nearest'`
  // is a no-op when the row is already visible, so mouse clicks don't jump. When a
  // group is expanded, items[0] appears both as the header and as a child row with
  // the same id — scroll the last match (the child, which is the highlighted one).
  useEffect(() => {
    if (!selectedItem) return;
    const matches = document.querySelectorAll(`[data-inbox-row="${selectedItem.conversationId}"]`);
    matches[matches.length - 1]?.scrollIntoView({ block: 'nearest' });
  }, [selectedItem]);

  // Set a conversation's unread count and keep its supplier-group aggregate
  // (groupUnreadTotal, shown on the group header) in sync optimistically — the
  // group total is decremented across all loaded siblings by the same delta.
  const setConversationUnread = useCallback((target: Item, newUnreadCount: number) => {
    const targetGroupKey = groupKey(target);
    setItems(prev => {
      const current = prev.find(i => i.conversationId === target.conversationId);
      if (!current) return prev;
      const delta = newUnreadCount - current.unreadCount;
      return prev.map(i => {
        if (i.conversationId === target.conversationId) {
          return { ...i, unreadCount: newUnreadCount, groupUnreadTotal: i.groupUnreadTotal + delta };
        }
        if (groupKey(i) === targetGroupKey) {
          return { ...i, groupUnreadTotal: i.groupUnreadTotal + delta };
        }
        return i;
      });
    });
    setSelectedItem(prev => prev?.conversationId === target.conversationId
      ? { ...prev, unreadCount: newUnreadCount }
      : prev);
  }, [groupKey]);

  // Coalesce rapid notification-counter refetches (e.g. marking several
  // conversations read/unread in quick succession) into a single trailing call.
  const notifRefreshRef = useRef<ReturnType<typeof setTimeout>>();
  const refreshNotificationsSoon = useCallback(() => {
    if (notifRefreshRef.current) clearTimeout(notifRefreshRef.current);
    notifRefreshRef.current = setTimeout(() => adapter.refreshBadge(), 400);
  }, [adapter]);

  const handleMarkRead = useCallback(async (item: Item) => {
    const prevUnread = item.unreadCount;
    // Read the whole thread (every unread message), not just the latest — keeps
    // the inbox count and the nav badge in sync.
    setConversationUnread(item, 0);
    try {
      await adapter.markRead(item);
      refreshNotificationsSoon();
    } catch {
      setConversationUnread(item, prevUnread);
    }
  }, [adapter, setConversationUnread, refreshNotificationsSoon]);

  const handleMarkUnread = useCallback(async (item: Item) => {
    const prevUnread = item.unreadCount;
    setConversationUnread(item, Math.max(1, item.unreadCount));
    try {
      await adapter.markUnread(item);
      refreshNotificationsSoon();
    } catch {
      setConversationUnread(item, prevUnread);
    }
  }, [adapter, setConversationUnread, refreshNotificationsSoon]);

  const openActivity = (item: Item) => (adapter.createActivity(item) ? () => setActivityItem(item) : undefined);

  const [confirmAllRead, setConfirmAllRead] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const handleMarkAllRead = async () => {
    if (markingAllRead) return;
    setMarkingAllRead(true);
    try {
      await adapter.markAllRead();
      refresh();
      refreshNotificationsSoon();
    } catch {
      // Nothing was marked — the list stays as it is.
    } finally {
      setMarkingAllRead(false);
      setConfirmAllRead(false);
    }
  };

  return (
    <div className={styles.root}>
      {/* One coherent control bar: search (flex) + Alla/Olästa segmented +
          a single Filter popover holding the type & project facets. */}
      <SearchBarWithActions
        value={searchQuery}
        onChange={handleSearchChange}
        style={{ marginBottom: 12 }}
      >
        <FilteringControl
          value={activeFilter}
          onChange={(v) => setActiveFilter(v as Filter)}
          options={[
            { value: 'all',    label: t('inbox.filter.all',    'Alla')   },
            { value: 'unread', label: t('inbox.filter.unread', 'Olästa') },
          ]}
        />
        <FiltersDropdown
          multiple
          checkboxes={filterCheckboxes}
          filtersData={filtersData}
          showFilter={showFilter}
          toggleFilter={setShowFilter}
          onSubmitFilter={handleSubmitFilters}
          onResetFilter={handleResetFilters}
        />
        <IconButton
          onClick={() => setConfirmAllRead(true)}
          icon={DoneAllIcon}
          title={t('inbox.markAllRead.action', 'Markera alla som lästa')}
          size={36} borderRadius={8} iconSize={18}
        />
      </SearchBarWithActions>

      <div className={styles.listCard}>
        {items.length === 0 && !loading && (
          <div className={styles.empty}>
            {activeFilter === 'unread'
              ? t('inbox.emptyUnread', 'Inga olästa meddelanden')
              : t('inbox.empty', 'Inga meddelanden')}
          </div>
        )}
        {groups.map(group => {
          // Every group always renders a header; conversations are children.
          const isExpanded = expandedGroups.has(group.key);
          return (
            <div key={group.key}>
              <InboxGroupHeader
                items={group.items}
                counterpart={adapter.counterpart(group.items[0])}
                sideLabel={adapter.sideLabel?.(group.items[0])}
                expanded={isExpanded}
                onToggle={() => toggleGroup(group.key)}
                onSelect={() => setSelectedItem(group.items[0])}
                isSelected={!isExpanded && group.items.some(i => i.conversationId === selectedItem?.conversationId)}
              />
              {isExpanded && group.items.map(item => (
                <InboxCard
                  key={item.conversationId}
                  item={item}
                  counterpart={adapter.counterpart(item)}
                  sideLabel={adapter.sideLabel?.(item)}
                  isSelected={selectedItem?.conversationId === item.conversationId}
                  onClick={() => setSelectedItem(item)}
                  onMarkRead={() => handleMarkRead(item)}
                  onMarkUnread={() => handleMarkUnread(item)}
                  onCreateActivity={openActivity(item)}
                />
              ))}
              {isExpanded && (() => {
                const total = group.items[0]?.groupConvCount ?? 0;
                const remaining = total - group.items.length;
                const isGrpLoading = groupLoading.has(group.key);
                if (remaining <= 0 && !isGrpLoading) return null;
                return (
                  <div className={styles.groupMore}>
                    {isGrpLoading
                      ? <span className={styles.groupMoreLoading}>{t('inbox.loading', 'Laddar...')}</span>
                      : <button
                          onClick={() => loadMoreForGroup(group.key, group.items)}
                          className={styles.loadMoreBtn}
                        >
                          {t('inbox.loadMore', 'Ladda {{count}} fler', { count: remaining })}
                        </button>
                    }
                  </div>
                );
              })()}
            </div>
          );
        })}
        {loading && (
          <div className={styles.loading}>
            {t('inbox.loading', 'Laddar...')}
          </div>
        )}
        <div ref={sentinelRef} className={styles.sentinel} />
      </div>

      <InboxPreviewPanel
        adapter={adapter}
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onMarkRead={() => selectedItem && handleMarkRead(selectedItem)}
        onMarkUnread={() => selectedItem && handleMarkUnread(selectedItem)}
        onCreateActivity={selectedItem ? openActivity(selectedItem) : undefined}
        onReplySent={refresh}
      />

      {activityItem && adapter.createActivity(activityItem) && (
        <ActivityModal
          initialTitle={`${activityItem.senderName} - ${activityItem.projectName}`}
          initialDescription={activityItem.preview}
          lockedProject={{ value: activityItem.projectId, label: activityItem.projectName }}
          onClose={() => setActivityItem(null)}
        />
      )}

      <Modal
        title={t('inbox.markAllRead.title', 'Markera alla som lästa?')}
        show={confirmAllRead}
        setShow={setConfirmAllRead}
        buttonInfo={{ label: t('inbox.markAllRead.confirm', 'Markera som lästa'), action: handleMarkAllRead, disabled: markingAllRead }}
        cancelButtonText={t('inbox.markAllRead.cancel', 'Avbryt')}
        unmountWhenHidden
      >
        {t('inbox.markAllRead.body', 'Alla meddelanden i inkorgen markeras som lästa.')}
      </Modal>
    </div>
  );
};

export default InboxTab;
